/**
 * Master-data field rules, as PURE functions.
 *
 * WHY A SEPARATE MODULE AND NOT CHECKS INSIDE THE SERVICES
 * -------------------------------------------------------
 * The same rules are needed in two places that must never disagree: the service,
 * which refuses a bad write, and the form, which explains the refusal while the
 * user is still typing. A rule implemented once and called from both is a rule
 * that cannot drift; a rule re-typed in each is a rule that eventually does.
 *
 * Every function here answers a question about TEXT and returns a verdict. None
 * throws (a validator that throws on a half-typed field takes the screen down),
 * none touches the database, and none reads a clock.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * -----------------------------
 * - **No default state.** A customer with no state is a customer whose place of
 *   supply is unresolved, and the POS resolver already reports that. Inventing a
 *   default here would quietly manufacture a tax liability in the wrong state.
 * - **No fuzzy duplicate matching.** "ABC Traders" and "ABC Trading" are often the
 *   same company and sometimes not, and a similarity score that merges them is a
 *   silent merge of two customers' ledgers. The only duplicates ruled out here are
 *   the ones with an objective identity: GSTIN and SKU.
 */
import { isGstinShaped, isValidStateCode, stateCodeFromGstin } from '@core/gst/stateCodes';
import { looksLikeAmount, required, type FieldValidity } from '@ui/lib/formValidation';

const OK: FieldValidity = { ok: true };

function fail(message: string): FieldValidity {
  return { ok: false, message };
}

/**
 * A GSTIN, when the user typed one.
 *
 * Empty is FINE and not an error: an unregistered walk-in customer is an ordinary
 * customer, and a form that refuses to save one teaches the user to type nonsense.
 * The shape is checked by `isGstinShaped` rather than by a second regex here, so
 * there is one answer to "is this a GSTIN" in the whole app.
 */
export function gstinField(value: string): FieldValidity {
  const text = value.trim();
  if (text === '') return OK;
  return isGstinShaped(text)
    ? OK
    : fail('GSTIN must be 15 characters, like 36AAAAA1234A1Z5.');
}

/**
 * A state code, when the user typed one.
 *
 * Reusing `isValidStateCode` is not pedantry: the place-of-supply resolver keys
 * on this exact list, and a code it does not recognise is a document the user
 * cannot issue.
 */
export function stateCodeField(value: string): FieldValidity {
  const text = value.trim();
  if (text === '') return OK;
  return isValidStateCode(text)
    ? OK
    : fail('That is not a valid GST state code.');
}

/**
 * Whether a state code and a GSTIN can belong together.
 *
 * A GSTIN's first two digits ARE its state. A GSTIN starting 29 (Karnataka) on a
 * customer registered in Telangana is not a typo the app should accept quietly —
 * it puts a Karnataka GSTIN on a Telangana supply and the split comes out wrong on
 * a real tax document. Reported, never corrected.
 */
export function stateMatchesGstin(
  stateCode: string | undefined,
  gstin: string | undefined,
): FieldValidity {
  const state = stateCode?.trim();
  const id = gstin?.trim().toUpperCase();
  if (!state || !id || !isGstinShaped(id)) return OK;
  const fromGstin = stateCodeFromGstin(id);
  if (!fromGstin) return OK;
  return fromGstin === state
    ? OK
    : fail(`This GSTIN belongs to state ${fromGstin}, not ${state}.`);
}

/**
 * An email address, when the user typed one.
 *
 * Deliberately a SHAPE check, not a deliverability check. Nothing here can send
 * mail, so a stricter rule would reject addresses that work while accepting none
 * that are proven. The shape is the part a typo actually breaks.
 */
export function emailField(value: string): FieldValidity {
  const text = value.trim();
  if (text === '') return OK;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(text)
    ? OK
    : fail('Enter a valid email address.');
}

/**
 * A phone number, when the user typed one.
 *
 * Digits, spaces, dashes, brackets and a leading +. Anything else is rejected
 * rather than stripped, because silently deleting characters from what someone
 * typed means the number saved is not the number they read back.
 */
export function phoneField(value: string): FieldValidity {
  const text = value.trim();
  if (text === '') return OK;
  if (!/^\+?[0-9][0-9\s\-()]{5,19}$/.test(text)) {
    return fail('Enter a valid phone number.');
  }
  const digits = text.replace(/\D/g, '');
  return digits.length >= 6 && digits.length <= 15
    ? OK
    : fail('A phone number must be 6 to 15 digits.');
}

/** A customer or supplier name. Required — the one thing a contact cannot lack. */
export function partyNameField(value: string): FieldValidity {
  return required(value, 'Customer name');
}

/** An item name. Required, for the same reason. */
export function productNameField(value: string): FieldValidity {
  return required(value, 'Item name');
}

/** A money field, reused from the shared validators so prices behave like every other amount. */
export function priceField(value: string, label: string): FieldValidity {
  return looksLikeAmount(value, label);
}

/**
 * An HSN/SAC code.
 *
 * GST codes are 4, 6 or 8 digits. Empty is fine: plenty of small items are billed
 * without one, and refusing to save them teaches the user to invent codes.
 */
export function hsnField(value: string): FieldValidity {
  const text = value.trim();
  if (text === '') return OK;
  return /^[0-9]{4}([0-9]{2}){0,2}$/.test(text)
    ? OK
    : fail('HSN/SAC must be 4, 6 or 8 digits.');
}

/**
 * A SKU / item code, when the user typed one.
 *
 * Shape only. Uniqueness is a question about the DATABASE, not about the text, so
 * it is answered by the service — a rule here could not see the other 4,000 items.
 */
export function skuField(value: string): FieldValidity {
  const text = value.trim();
  if (text === '') return OK;
  // Printable, no spaces: a SKU is a label for a barcode label and for search, and
  // a space in it defeats both on the devices a shop actually uses.
  return /^[A-Za-z0-9._/-]{1,64}$/.test(text)
    ? OK
    : fail('Item code may use letters, numbers, dot, dash, slash and underscore.');
}

/** A unit of measure. Required, because a quantity with no unit is unreadable on paper. */
export function unitField(value: string): FieldValidity {
  return required(value, 'Unit');
}